import React, { useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';
import { getClusterLabelConfig } from '../../../services/patternClustering.service';

/**
 * ClusterVisualizationChart - 2D scatter plot showing cluster distribution
 */
const ClusterVisualizationChart = ({
  visualization,
  loading = false,
  highlightPersonId = null,
  height = 400,
}) => {
  // Group points by cluster
  const { clusterData, centroids, clusterColors } = useMemo(() => {
    if (!visualization?.points) {
      return { clusterData: {}, centroids: [], clusterColors: {} };
    }

    const data = {};
    const colors = {};

    // Group points by cluster
    visualization.points.forEach((point) => {
      const clusterId = point.clusterId;
      if (!data[clusterId]) {
        data[clusterId] = [];
        const config = getClusterLabelConfig(point.clusterLabel);
        colors[clusterId] = config.color;
      }
      data[clusterId].push({
        x: point.x,
        y: point.y,
        personId: point.personId,
        clusterLabel: point.clusterLabel,
        isHighlighted: point.personId === highlightPersonId,
      });
    });

    return {
      clusterData: data,
      centroids: visualization.clusterCentroids || [],
      clusterColors: colors,
    };
  }, [visualization, highlightPersonId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div
          className="bg-gray-100 rounded flex items-center justify-center"
          style={{ height }}
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!visualization?.points?.length) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          Cluster Visualization
        </h3>
        <div
          className="bg-gray-50 rounded flex items-center justify-center text-gray-500"
          style={{ height }}
        >
          <div className="text-center">
            <div className="text-4xl mb-2">📊</div>
            <p className="text-sm">No visualization data available.</p>
            <p className="text-xs mt-1">Train the clustering model first.</p>
          </div>
        </div>
      </div>
    );
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const config = getClusterLabelConfig(data.clusterLabel);
      return (
        <div className="bg-white border border-gray-200 rounded shadow-lg p-2 text-xs">
          <div className="font-semibold">Person ID: {data.personId}</div>
          <div className="flex items-center gap-1 mt-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: config.color }}
            ></div>
            <span>{config.label}</span>
          </div>
          <div className="text-gray-500 mt-1">
            ({data.x.toFixed(3)}, {data.y.toFixed(3)})
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">
          Cluster Visualization
        </h3>
        <div className="text-xs text-gray-500">
          {visualization.visualizationMethod?.toUpperCase() || 'UMAP'} • {visualization.totalPoints} points
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            type="number"
            dataKey="x"
            name="X"
            tick={{ fontSize: 10 }}
            tickLine={false}
          />
          <YAxis
            type="number"
            dataKey="y"
            name="Y"
            tick={{ fontSize: 10 }}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            wrapperStyle={{ paddingTop: 10 }}
          />

          {/* Render each cluster */}
          {Object.entries(clusterData).map(([clusterId, points]) => {
            const config = getClusterLabelConfig(points[0]?.clusterLabel);
            return (
              <Scatter
                key={clusterId}
                name={config.label}
                data={points}
                fill={clusterColors[clusterId]}
                fillOpacity={0.7}
                shape={(props) => {
                  const { cx, cy, payload } = props;
                  const isHighlighted = payload.isHighlighted;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={isHighlighted ? 8 : 5}
                      fill={clusterColors[payload.clusterId || clusterId]}
                      fillOpacity={isHighlighted ? 1 : 0.7}
                      stroke={isHighlighted ? '#000' : 'none'}
                      strokeWidth={isHighlighted ? 2 : 0}
                    />
                  );
                }}
              />
            );
          })}

          {/* Render centroids */}
          {centroids.map((centroid) => (
            <ReferenceDot
              key={`centroid-${centroid.clusterId}`}
              x={centroid.x}
              y={centroid.y}
              r={12}
              fill="white"
              stroke={getClusterLabelConfig(centroid.label).color}
              strokeWidth={3}
              isFront={true}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>

      {/* Legend for centroids */}
      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 justify-center">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full border-2 border-gray-400 bg-white"></div>
          <span>Cluster Centroid</span>
        </div>
        {highlightPersonId && (
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-full bg-gray-400 border-2 border-black"></div>
            <span>Selected Person</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClusterVisualizationChart;
